import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  Modal,
  Pressable,
  Dimensions,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { studentHasCourseName } from '../utils/studentCourses';
import { hasSuperUserAccess } from '../utils/sessionUser';
import {
  DIM_MAK_COURSE_NAME,
  dimMakMeridianImageCandidates,
  meridianFromScreenName,
  meridianFromSlug,
} from '../utils/dimMakMeridians';

const SHOW_ALL = 'all';

export default function DimMakMeridianPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const user = route?.params?.user ?? null;
  const courseId = route?.params?.courseId ?? null;
  const courseName = route?.params?.courseName ?? DIM_MAK_COURSE_NAME;
  const catalog =
    meridianFromScreenName(route?.name) ||
    meridianFromSlug(route?.params?.slug) ||
    null;
  const slug = catalog?.slug || route?.params?.slug || '';
  const fallbackTitle = catalog?.name || route?.params?.meridianName || 'Dim Mak';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [meridian, setMeridian] = useState(null);
  const [points, setPoints] = useState([]);
  const [filterValue, setFilterValue] = useState(SHOW_ALL);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoom, setZoom] = useState(1);

  const imageCandidates = useMemo(() => dimMakMeridianImageCandidates(meridian), [meridian]);
  const imageUrl = imageCandidates[imageIndex] || imageCandidates[0] || '';

  const totalForDropdown = useMemo(() => {
    const fromRecord = meridian?.total_dim_mak_points != null
      ? Number(meridian.total_dim_mak_points)
      : 0;
    const fromPoints = points.reduce(
      (max, p) => Math.max(max, Number(p.dim_mak_number) || 0),
      0
    );
    return Math.max(fromRecord, fromPoints);
  }, [meridian, points]);

  const filterOptions = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalForDropdown; n += 1) nums.push(n);
    return nums;
  }, [totalForDropdown]);

  const visiblePoints = useMemo(() => {
    const ordered = [...points].sort(
      (a, b) => (a.dim_mak_number || 0) - (b.dim_mak_number || 0)
    );
    if (filterValue === SHOW_ALL) return ordered;
    return ordered.filter((p) => Number(p.dim_mak_number) === Number(filterValue));
  }, [points, filterValue]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (courseId == null || user?.id == null || !slug) {
        setLoading(false);
        setError(true);
        return;
      }
      if (!hasSuperUserAccess(user) && !studentHasCourseName(user, courseName)) {
        setLoading(false);
        setError(true);
        return;
      }
      setLoading(true);
      setError(false);
      try {
        const r = await fetch(
          `${getApiUrl()}/api/courses/${encodeURIComponent(String(courseId))}/dim-mak/meridians/${encodeURIComponent(slug)}?viewer_id=${encodeURIComponent(String(user.id))}`
        );
        if (!r.ok) {
          if (!cancelled) {
            setMeridian(null);
            setPoints([]);
            setError(r.status !== 404);
          }
          return;
        }
        const data = await r.json();
        if (cancelled) return;
        setMeridian(data);
        setPoints(Array.isArray(data?.points) ? data.points : []);
        setFilterValue(SHOW_ALL);
        setImageIndex(0);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [courseId, user, courseName, slug]);

  const goBack = useCallback(() => {
    navigation.navigate('DimMakHandbook', {
      user,
      courseId,
      courseName: courseName || DIM_MAK_COURSE_NAME,
    });
  }, [navigation, user, courseId, courseName]);

  const openZoom = () => {
    if (!imageUrl) return;
    setZoom(1);
    setZoomOpen(true);
  };

  const changeZoom = useCallback((delta) => {
    setZoom((current) => Math.min(4, Math.max(1, Number((current + delta).toFixed(2)))));
  }, []);

  const zoomBase = Math.min(Math.max(Dimensions.get('window').width - 72, 240), 640);
  const scaledSize = Math.round(zoomBase * zoom);

  const handleClear = () => {
    setFilterValue(SHOW_ALL);
    setShowFilterDropdown(false);
  };

  const title = meridian?.meridian_name || fallbackTitle;
  const styles = useMemo(() => getStyles(theme), [theme]);
  const filterLabel = filterValue === SHOW_ALL ? 'Show all' : String(filterValue);
  const showNumberWithDescription = filterValue === SHOW_ALL;

  return (
    <View
      style={styles.container}
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'dim-mak-meridian' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header} data-header="true">
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <TouchableOpacity
          style={styles.breadcrumbContainer}
          onPress={goBack}
          activeOpacity={0.7}
        >
          <Text style={styles.breadcrumbBack} numberOfLines={1}>
            ‹ Dim Mak handbook
          </Text>
        </TouchableOpacity>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={user} />
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        keyboardShouldPersistTaps="handled"
      >
        <Text style={styles.pageTitle}>{title}</Text>

        {loading ? (
          <View style={styles.centerBlock}>
            <ActivityIndicator color={theme.colors.heading} />
          </View>
        ) : error ? (
          <Text style={styles.body}>This meridian page could not be loaded.</Text>
        ) : !meridian ? (
          <Text style={styles.body}>
            No {fallbackTitle} meridian record has been created yet. Use Dim mak builder to add it.
          </Text>
        ) : (
          <>
            {imageUrl ? (
              <TouchableOpacity
                style={styles.imageWrap}
                onPress={openZoom}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel={`Open ${title} image`}
              >
                <Image
                  source={{ uri: imageUrl }}
                  style={styles.mainImage}
                  contentFit="contain"
                  onError={() => {
                    setImageIndex((i) => (i + 1 < imageCandidates.length ? i + 1 : i));
                  }}
                />
                <Text style={styles.imageHint}>Tap to enlarge</Text>
              </TouchableOpacity>
            ) : (
              <Text style={styles.body}>No meridian image has been added yet.</Text>
            )}

            <View style={styles.filterBlock}>
              <Text style={styles.label}>Points</Text>
              <TouchableOpacity
                style={styles.dropdownTrigger}
                onPress={() => setShowFilterDropdown((open) => !open)}
                {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
              >
                <Text style={styles.dropdownTriggerText}>{filterLabel}</Text>
                <Text style={styles.dropdownChevron}>{showFilterDropdown ? '▲' : '▼'}</Text>
              </TouchableOpacity>
              {showFilterDropdown ? (
                <View
                  style={styles.dropdownList}
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                >
                  <TouchableOpacity
                    style={styles.dropdownRow}
                    onPress={() => {
                      setFilterValue(SHOW_ALL);
                      setShowFilterDropdown(false);
                    }}
                    {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                  >
                    <Text style={styles.dropdownRowText}>Show all</Text>
                  </TouchableOpacity>
                  {filterOptions.map((n) => (
                    <TouchableOpacity
                      key={n}
                      style={styles.dropdownRow}
                      onPress={() => {
                        setFilterValue(n);
                        setShowFilterDropdown(false);
                      }}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                    >
                      <Text style={styles.dropdownRowText}>{n}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              ) : null}
              <TouchableOpacity
                style={styles.clearButton}
                onPress={handleClear}
                accessibilityRole="button"
                accessibilityLabel="Clear point filter"
              >
                <Text style={styles.clearButtonText}>Clear</Text>
              </TouchableOpacity>
            </View>

            <View style={styles.pointsList}>
              {visiblePoints.length === 0 ? (
                <Text style={styles.body}>
                  {filterValue === SHOW_ALL
                    ? 'No Dim Mak points have been added for this meridian yet.'
                    : `No description for point ${filterValue}.`}
                </Text>
              ) : (
                visiblePoints.map((point) => (
                  <View key={point.id} style={styles.pointCard}>
                    {showNumberWithDescription ? (
                      <Text style={styles.pointNumber}>{point.dim_mak_number}</Text>
                    ) : null}
                    <Text style={styles.pointDescription}>
                      {point.dim_mak_point_description || 'No description yet.'}
                    </Text>
                  </View>
                ))
              )}
            </View>
          </>
        )}
      </ScrollView>

      <Modal visible={zoomOpen} transparent animationType="fade" onRequestClose={() => setZoomOpen(false)}>
        <View
          style={styles.zoomOverlay}
          pointerEvents="box-none"
          {...(Platform.OS === 'web' ? { dataSet: { dimMakZoomModal: 'true' } } : {})}
        >
          <Pressable
            style={styles.zoomDismiss}
            onPress={() => setZoomOpen(false)}
            {...(Platform.OS === 'web' ? { dataSet: { dimMakZoomDismiss: 'true' } } : {})}
          />
          <View
            style={styles.zoomSheet}
            pointerEvents="auto"
            {...(Platform.OS === 'web' ? { dataSet: { dimMakZoomSheet: 'true' } } : {})}
          >
            <View
              style={styles.zoomToolbar}
              {...(Platform.OS === 'web' ? { dataSet: { dimMakZoomToolbar: 'true' } } : {})}
            >
              <TouchableOpacity
                style={styles.zoomBtn}
                onPress={() => changeZoom(-0.5)}
                accessibilityLabel="Zoom out"
              >
                <Text style={styles.zoomBtnText}>−</Text>
              </TouchableOpacity>
              <Text style={styles.zoomLabel}>{Math.round(zoom * 100)}%</Text>
              <TouchableOpacity
                style={styles.zoomBtn}
                onPress={() => changeZoom(0.5)}
                accessibilityLabel="Zoom in"
              >
                <Text style={styles.zoomBtnText}>+</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.zoomClose} onPress={() => setZoomOpen(false)}>
                <Text style={styles.zoomCloseText}>Close</Text>
              </TouchableOpacity>
            </View>
            <ScrollView
              style={styles.zoomScroll}
              contentContainerStyle={[
                styles.zoomScrollContent,
                { minWidth: scaledSize + 24, minHeight: scaledSize + 24 },
              ]}
              maximumZoomScale={4}
              minimumZoomScale={1}
              bouncesZoom
              nestedScrollEnabled
              {...(Platform.OS === 'web' ? { dataSet: { dimMakZoomScroller: 'true' } } : {})}
            >
              <ScrollView
                horizontal
                nestedScrollEnabled
                bounces={false}
                contentContainerStyle={styles.zoomHScrollContent}
              >
                {imageUrl ? (
                  <Image
                    source={{ uri: imageUrl }}
                    style={{ width: scaledSize, height: scaledSize }}
                    contentFit="contain"
                    {...(Platform.OS === 'web' ? { dataSet: { dimMakZoomImage: 'true' } } : {})}
                  />
                ) : null}
              </ScrollView>
            </ScrollView>
          </View>
        </View>
      </Modal>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    hamburgerContainer: { marginRight: 15 },
    breadcrumbContainer: { flex: 1, minWidth: 0, backgroundColor: 'transparent' },
    breadcrumbBack: {
      fontSize: 17,
      fontWeight: '600',
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    profileIconContainer: {},
    content: { flex: 1 },
    contentContainer: {
      paddingHorizontal: 20,
      paddingTop: 12,
      paddingBottom: 40,
    },
    centerBlock: { paddingVertical: 32, alignItems: 'center' },
    pageTitle: {
      fontSize: 28,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 18,
      backgroundColor: 'transparent',
    },
    imageWrap: {
      width: '100%',
      maxWidth: 640,
      alignSelf: 'center',
      marginBottom: 20,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 12,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    mainImage: {
      width: '100%',
      height: 280,
      backgroundColor: 'transparent',
    },
    imageHint: {
      textAlign: 'center',
      paddingVertical: 8,
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.7,
      backgroundColor: 'transparent',
    },
    filterBlock: {
      maxWidth: 640,
      alignSelf: 'center',
      width: '100%',
      marginBottom: 16,
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    dropdownTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 8,
      padding: 12,
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    dropdownTriggerText: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownChevron: {
      fontSize: 14,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownList: {
      marginTop: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      maxHeight: 260,
    },
    dropdownRow: {
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    dropdownRowText: {
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    clearButton: {
      marginTop: 10,
      alignSelf: 'flex-start',
      backgroundColor: '#9e9e9e',
      paddingVertical: 10,
      paddingHorizontal: 18,
      borderRadius: 8,
    },
    clearButtonText: {
      color: '#000',
      fontWeight: '700',
      fontSize: 15,
      backgroundColor: 'transparent',
    },
    pointsList: {
      maxWidth: 640,
      alignSelf: 'center',
      width: '100%',
    },
    pointCard: {
      paddingVertical: 14,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.isDarkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0',
      backgroundColor: 'transparent',
    },
    pointNumber: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.colors.heading,
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    pointDescription: {
      fontSize: 15,
      lineHeight: 22,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    body: {
      fontSize: 15,
      lineHeight: 22,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    zoomOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.72)',
      justifyContent: 'center',
      padding: 16,
    },
    zoomDismiss: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'transparent',
      zIndex: 0,
    },
    zoomSheet: {
      flex: 1,
      maxHeight: '92%',
      backgroundColor: theme.isDarkMode ? '#111' : '#FFFFFF',
      borderRadius: 12,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: theme.colors.border,
      zIndex: 2,
      position: 'relative',
    },
    zoomToolbar: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: 12,
      gap: 10,
      backgroundColor: theme.isDarkMode ? '#1c1c1c' : '#F5F5F5',
      zIndex: 3,
    },
    zoomBtn: {
      width: 44,
      height: 44,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.isDarkMode ? '#333' : '#E0E0E0',
    },
    zoomBtnText: {
      fontSize: 22,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    zoomLabel: {
      fontSize: 15,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
      minWidth: 52,
      textAlign: 'center',
    },
    zoomClose: {
      marginLeft: 'auto',
      backgroundColor: '#9e9e9e',
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 8,
    },
    zoomCloseText: {
      color: '#000',
      fontWeight: '700',
      backgroundColor: 'transparent',
    },
    zoomScroll: { flex: 1, minHeight: 0 },
    zoomScrollContent: {
      padding: 12,
      alignItems: 'center',
      justifyContent: 'center',
    },
    zoomHScrollContent: {
      alignItems: 'center',
      justifyContent: 'center',
    },
  });
}
